import { useCallback, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { ZoomIn, ZoomOut, Check } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";

const createImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

export async function getCroppedBlob(src: string, area: Area): Promise<Blob> {
  const img = await createImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, 512, 512);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Crop failed"))), "image/jpeg", 0.92));
}

export const AvatarCropDialog = ({ src, open, busy, onCancel, onSave }: {
  src: string | null; open: boolean; busy: boolean;
  onCancel: () => void; onSave: (area: Area) => void;
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => setArea(pixels), []);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent className="bg-[#0d0d12] border-white/10 text-white sm:max-w-md" data-testid="avatar-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Frame your photo</DialogTitle>
          <DialogDescription className="text-gray-500 text-sm">
            Drag to reposition and zoom until your face fills the circle — this is exactly how your byline will look.
          </DialogDescription>
        </DialogHeader>
        <div className="relative h-72 w-full overflow-hidden rounded-xl bg-black" data-testid="avatar-crop-area">
          {src && (
            <Cropper
              image={src} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
          )}
        </div>
        <div className="flex items-center gap-3 mt-1">
          <ZoomOut className="h-4 w-4 text-gray-500 shrink-0" />
          <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={(v: number[]) => setZoom(v[0])}
            className="flex-1" data-testid="avatar-crop-zoom" />
          <ZoomIn className="h-4 w-4 text-gray-500 shrink-0" />
        </div>
        <div className="flex justify-end gap-3 mt-2">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="avatar-crop-cancel"
            className="border-white/20 bg-transparent text-white hover:bg-white/5">
            Cancel
          </Button>
          <Button onClick={() => area && onSave(area)} disabled={busy || !area} data-testid="avatar-crop-save"
            className="bg-[#7C3AED] text-white font-bold hover:bg-[#6a2dd0]">
            <Check className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : "Save photo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
